import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Skeleton,
  Snackbar,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import type { DataSourceHealth } from '../../types/dataHealth.types';
import type { IntegrationStatus } from '../../services/siteIntegrationService';
import { initiateOAuthFlow } from '../../utils/oauthPopupHelper';

interface BingSetupPanelProps {
  health?: DataSourceHealth;
  isLoading: boolean;
  siteId?: number;
  siteName?: string;
  /** Pre-fetched integration status from unified endpoint (optional) */
  integrationStatus?: IntegrationStatus;
}

export const BingSetupPanel: React.FC<BingSetupPanelProps> = ({ health, isLoading, siteId, siteName, integrationStatus }) => {
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handleOAuthCallback = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'oauth-callback' && event.data?.provider === 'bing') {
        if (event.data.status === 'success') {
          setSnackbar({ open: true, message: 'Bing Webmaster Tools connected successfully!', severity: 'success' });
          setTimeout(() => window.location.reload(), 1500);
        } else {
          setSnackbar({ open: true, message: event.data.message || 'OAuth connection failed', severity: 'error' });
        }
      }
    };
    window.addEventListener('message', handleOAuthCallback);
    return () => window.removeEventListener('message', handleOAuthCallback);
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ px: 3 }}>
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  // Use integration status if available, fallback to health status
  const isConnected = integrationStatus?.connected ?? false;
  const isExpired = integrationStatus?.expired ?? false;
  const isConfigured = isConnected && !isExpired;

  return (
    <Box sx={{ px: 3 }}>
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Bing Webmaster Tools Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect Bing Webmaster Tools to expand SEO insights beyond Google and capture Bing search data{siteName ? ` for ${siteName}` : ''}.
              </Typography>
            </Box>
            <Chip
              label={isConfigured ? 'Configured' : isExpired ? 'Token Expired' : 'Not Configured'}
              color={isConfigured ? 'success' : isExpired ? 'warning' : 'default'}
              icon={isConfigured ? <CheckIcon /> : isExpired ? <ScheduleIcon /> : <ErrorIcon />}
            />
          </Box>

          <Box my={3}>
            <Typography variant="subtitle2" gutterBottom>
              What you'll get:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Bing search performance data (clicks, impressions)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Cross-search-engine trend comparison" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Bing-specific crawl and indexing insights" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Multi-channel SEO opportunity identification" />
              </ListItem>
            </List>
          </Box>

          <Alert severity={isConfigured ? 'success' : isExpired ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {isConfigured ? (
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  ✓ Bing Webmaster Tools Connected for this site
                </Typography>
                {integrationStatus?.expiresAt && (
                  <Typography variant="caption">
                    Token expires: {new Date(integrationStatus.expiresAt).toLocaleString()}
                  </Typography>
                )}
                {health && (
                  <Typography variant="caption" display="block">
                    Last Data Sync: {health.lastSuccessfulSync ? new Date(health.lastSuccessfulSync).toLocaleString() : 'Never'}
                  </Typography>
                )}
              </Box>
            ) : isExpired ? (
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  ⚠ Bing Token Expired - Reconnection Required
                </Typography>
                <Typography variant="caption" display="block">
                  {integrationStatus?.message || 'Your OAuth token has expired. Please reconnect to continue syncing data.'}
                </Typography>
                {integrationStatus?.expiresAt && (
                  <Typography variant="caption" display="block">
                    Token expired: {new Date(integrationStatus.expiresAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2">
                Bing Webmaster Tools is not connected for this site. Complete OAuth authentication to start importing Bing data.
              </Typography>
            )}
          </Alert>

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => siteId && initiateOAuthFlow('bing', siteId)}
              disabled={!siteId}
            >
              {isConfigured ? 'Reconnect Bing' : 'Connect Bing'}
            </Button>
            {isConfigured && (
              <Button
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={() => navigate('/data-health')}
              >
                View Sync History
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
